import type { JournalArticle } from "./journal-types";

/** Cluster: bijzondere bestemmingen (algemeen, zonder specifieke locaties). */
export const BESTEMMINGEN_ARTICLES: JournalArticle[] = [
  {
    slug: "taxi-naar-het-ziekenhuis",
    title: "Met de taxi naar het ziekenhuis: waar moet u op letten?",
    seoTitle: "Taxi naar het ziekenhuis: waar op letten? | Taxiritje.nl",
    description:
      "Een taxi naar het ziekenhuis regelen: timing, de juiste ingang, terugrit na een behandeling en waar u op moet letten bij het reserveren.",
    category: "bestemmingen",
    intent: "Praktisch: vervoer regelen voor een ziekenhuisbezoek of behandeling.",
    primaryKeyword: "taxi naar het ziekenhuis",
    published: "2026-08-14",
    readingMinutes: 5,
    intro:
      "Een ziekenhuisbezoek is zelden een gewone rit. U heeft een afspraaktijd die vaststaat, u bent misschien niet in staat zelf te rijden, en na een behandeling weet u niet precies wanneer u klaar bent. Met een paar keuzes bij het boeken maakt u die rit een stuk voorspelbaarder.",
    sections: [
      {
        heading: "Plan op de afspraaktijd, niet op de rijtijd",
        paragraphs: [
          "Ziekenhuizen liggen vaak aan drukke invalswegen en hebben een eigen verkeerscirculatie. Tel bij de reistijd daarom altijd tijd op voor de laatste honderden meters, het uitstappen en het lopen naar de juiste afdeling. Wees bij een eerste bezoek extra ruim: het vinden van de juiste balie kost tijd.",
        ],
        bullets: [
          "Afspraaktijd min inlooptijd in het gebouw",
          "min reistijd inclusief spitsmarge",
          "= ophaaltijd",
        ],
      },
      {
        heading: "Geef de juiste ingang door",
        paragraphs: [
          "Grote ziekenhuizen hebben meerdere ingangen: een hoofdingang, een poliklinische ingang en soms een aparte ingang voor dagbehandeling. Noteer bij het boeken het exacte adres met huisnummer, en vermeld in een opmerking welke ingang u nodig heeft. Dat scheelt bij aankomst zoekwerk en loopafstand.",
        ],
      },
      {
        heading: "De terugrit na een onderzoek of behandeling",
        paragraphs: [
          "Na een ingreep of onderzoek is het eindtijdstip vaak onzeker. Twee benaderingen werken in de praktijk goed: een terugrit boeken met een ruime marge na de verwachte eindtijd, of pas boeken zodra u weet dat u naar buiten kunt. Bent u niet in staat zelf te rijden — bijvoorbeeld na een verdoving — dan is een gereserveerde rit de veiligste optie.",
        ],
      },
      {
        heading: "Vergoeding en zittend ziekenvervoer",
        paragraphs: [
          "In bepaalde situaties kunt u in aanmerking komen voor een vergoeding van vervoer naar een behandeling via uw zorgverzekering; dit heet zittend ziekenvervoer en er gelden landelijke voorwaarden. Wij verzorgen dat niet automatisch — informeer bij uw zorgverzekeraar of uw situatie hieronder valt en welke voorwaarden gelden.",
        ],
      },
    ],
    conclusion:
      "Boek ruimer dan u denkt nodig te hebben, geef de juiste ingang door en regel de terugrit bewust. Zo blijft het ziekenhuisbezoek zelf het enige waar u aan hoeft te denken.",
    faq: [
      {
        q: "Kan ik de terugrit uit het ziekenhuis later boeken?",
        a: "Dat kan verstandig zijn wanneer de eindtijd onzeker is. Weet u wel ongeveer hoe lang uw bezoek duurt, dan geeft een vooraf gereserveerde rit met marge de meeste rust.",
      },
      {
        q: "Wordt vervoer naar het ziekenhuis vergoed?",
        a: "Soms, via de regeling voor zittend ziekenvervoer. De voorwaarden en aanvraag lopen via uw zorgverzekeraar; informeer daar vooraf naar.",
      },
    ],
    links: [
      { label: "Taxi boeken met vaste prijs", to: "/boeken" },
      { label: "Waarom een taxi handig is als u slecht ter been bent", to: "/journal/reizen-als-u-slecht-ter-been-bent" },
      { label: "Contact opnemen", to: "/contact" },
    ],
    sources: [
      {
        label: "Zittend ziekenvervoer: voorwaarden (Rijksoverheid)",
        url: "https://www.rijksoverheid.nl/themas/familie-zorg-en-gezondheid/zorgverzekering",
      },
    ],
  },
  {
    slug: "taxi-naar-een-stadion-of-evenement",
    title: "Taxi naar een stadion of evenement: zo plant u uw reis",
    seoTitle: "Taxi naar een stadion of evenement: zo plant u het | Taxiritje.nl",
    description:
      "Naar een wedstrijd, concert of festival? Zo kiest u een slim ophaalpunt, vermijdt u drukte en regelt u uw terugrit op tijd.",
    category: "bestemmingen",
    intent: "Praktisch: vervoer plannen rond drukke evenementen.",
    primaryKeyword: "taxi naar stadion",
    published: "2026-08-17",
    readingMinutes: 5,
    intro:
      "Rond stadions en evenemententerreinen gelden vaak tijdelijke verkeersmaatregelen: afgesloten straten, eenrichtingsverkeer en verplaatste in- en uitstapplaatsen. Wie daar rekening mee houdt, staat na afloop niet een uur te wachten.",
    sections: [
      {
        heading: "Kom eerder aan dan de massa",
        paragraphs: [
          "De drukte concentreert zich in het laatste half uur voor aanvang. Plan uw aankomst ruim daarvoor: u rijdt dan nog door zonder omleidingen en hoeft niet de laatste meters te lopen tussen duizenden anderen.",
        ],
      },
      {
        heading: "Kies een in- en uitstappunt op afstand",
        paragraphs: [
          "Bij grote evenementen is de directe omgeving vaak niet toegankelijk voor voertuigen. Spreek daarom een herkenbaar punt op enkele minuten lopen af: een kruising, een parkeerterrein of een bekende straat net buiten het afgesloten gebied. Controleer vooraf op de website van de organisatie of gemeente welke wegen dicht zijn.",
        ],
        bullets: [
          "Zoek een punt met een duidelijke naam en huisnummer",
          "Vermijd de directe toegangspoorten",
          "Spreek met uw gezelschap één vast punt af",
        ],
      },
      {
        heading: "Regel de terugrit vóórdat u naar binnen gaat",
        paragraphs: [
          "Na een wedstrijd of concert wil iedereen tegelijk weg. Een vooraf gereserveerde terugrit is dan het verschil tussen instappen en wachten. Kies een tijdstip iets ná het officiële einde, zodat u niet hoeft te haasten en de eerste drukte al is weggetrokken.",
        ],
      },
      {
        heading: "Reist u met een groep?",
        paragraphs: [
          "Bij vijf of meer personen is één groter voertuig doorgaans overzichtelijker en per persoon voordeliger dan losse ritten. Geef het exacte aantal personen op, zodat het juiste voertuigtype wordt ingezet.",
        ],
      },
    ],
    conclusion:
      "Kom vroeg aan, spreek een ophaalpunt net buiten het drukke gebied af en boek de terugrit voordat het evenement begint. Zo eindigt de avond niet met wachten in de massa.",
    faq: [
      {
        q: "Waarom kan een taxi niet tot de ingang rijden?",
        a: "Rond evenementen worden straten tijdelijk afgesloten voor de veiligheid en doorstroming. Een ophaalpunt net buiten dat gebied is daarom sneller dan wachten op toegang.",
      },
      {
        q: "Is vooraf reserveren echt nodig?",
        a: "Bij evenementen wel. De vraag naar vervoer piekt direct na afloop, terwijl een gereserveerde rit al voor u is ingepland.",
      },
    ],
    links: [
      { label: "Reizen met een groep", to: "/journal/reizen-met-een-groep" },
      { label: "Taxi boeken", to: "/boeken" },
      { label: "Bekijk het werkgebied", to: "/taxi" },
    ],
  },
  {
    slug: "taxi-naar-een-hotel",
    title: "Met de taxi naar een hotel: wat handig is om te weten",
    seoTitle: "Met de taxi naar een hotel: praktische tips | Taxiritje.nl",
    description:
      "Naar of vanaf een hotel met de taxi: het juiste adres opgeven, aankomst met bagage, check-outtijden en de rit terug goed plannen.",
    category: "bestemmingen",
    intent: "Praktisch: vervoer van en naar hotelovernachtingen.",
    primaryKeyword: "taxi naar hotel",
    published: "2026-08-19",
    readingMinutes: 4,
    intro:
      "Een hotelrit lijkt eenvoudig, maar juist hier gaat het geregeld mis: hotels met bijna dezelfde naam, ingangen aan een andere straat en een check-outtijd die niet aansluit op uw vervoer.",
    sections: [
      {
        heading: "Geef het volledige adres op, niet alleen de naam",
        paragraphs: [
          "Hotelketens hebben vaak meerdere vestigingen in dezelfde regio. Vul daarom altijd straat, huisnummer, postcode en plaats in. Zo staat de rit gegarandeerd op het juiste pand gepland en is de prijs ook op dat adres berekend.",
        ],
      },
      {
        heading: "Let op de ingang en de aanrijroute",
        paragraphs: [
          "In binnensteden ligt de hoteltoegang soms in een voetgangersgebied of aan een zijstraat. Vermeld bij het boeken waar u wilt in- of uitstappen als u dat weet; anders wordt gewoon het opgegeven adres aangehouden.",
        ],
      },
      {
        heading: "Stem de rit af op check-in en check-out",
        paragraphs: [
          "Veel hotels hanteren een check-out in de late ochtend en check-in in de middag. Boekt u de rit vóór check-in, dan kunt u meestal uw bagage bij het hotel achterlaten. Voor de terugreis is het slim om een marge tussen check-out en de ophaaltijd te nemen.",
        ],
        bullets: [
          "Check-out meestal tussen 10:00 en 12:00 uur",
          "Bagage vaak te bewaren bij de receptie",
          "Bij vroege vertrektijden: check of de receptie 24 uur open is",
        ],
      },
      {
        heading: "Zakelijk verblijf",
        paragraphs: [
          "Reist u zakelijk, dan is het praktisch om de rit op naam en met bedrijfsgegevens te boeken, zodat de factuur meteen klopt en u niets hoeft na te sturen bij het declareren.",
        ],
      },
    ],
    conclusion:
      "Vul altijd het volledige hoteladres in, houd rekening met check-outtijden en boek de terugrit met marge. Dan is de rit het makkelijkste onderdeel van uw verblijf.",
    faq: [
      {
        q: "Waarom is de hotelnaam alleen niet genoeg?",
        a: "Ketens hebben vaak meerdere vestigingen dicht bij elkaar. Met straat, huisnummer en postcode weet u zeker dat de rit naar het juiste pand is gepland en juist is geprijsd.",
      },
      {
        q: "Kan ik een rit boeken voordat ik kan inchecken?",
        a: "Ja. De meeste hotels bewaren uw bagage tot de check-in, waardoor u de tijd tussen aankomst en check-in vrij kunt besteden.",
      },
    ],
    links: [
      { label: "Zakelijk vervoer", to: "/zakelijk" },
      { label: "Taxi boeken", to: "/boeken" },
      { label: "Zakelijk taxivervoer en facturatie", to: "/journal/zakelijk-taxivervoer-facturatie" },
    ],
  },
  {
    slug: "taxi-naar-een-station",
    title: "Taxi naar een station: wanneer is dat de beste keuze?",
    seoTitle: "Taxi naar een station: wanneer is dat slim? | Taxiritje.nl",
    description:
      "Een taxi naar het station combineren met de trein: wanneer dat tijd en stress bespaart, hoeveel marge u aanhoudt en waar u instapt.",
    category: "bestemmingen",
    intent: "Praktisch: voortransport naar het station regelen.",
    primaryKeyword: "taxi naar station",
    published: "2026-08-20",
    readingMinutes: 4,
    intro:
      "Een taxi en de trein zijn geen concurrenten. Voor veel reizen is de combinatie juist het slimst: met de taxi naar het station en met de trein de lange afstand — vooral wanneer u bagage heeft of vroeg vertrekt.",
    sections: [
      {
        heading: "Wanneer voortransport per taxi loont",
        paragraphs: [
          "Woont u niet op loopafstand van een station, reist u met koffers of vertrekt u voordat de eerste bus rijdt, dan haalt een taxi precies het lastigste deel van de reis weg. U hoeft niet met bagage over te stappen en komt met marge op het perron aan.",
        ],
        bullets: [
          "Vroeg vertrek voordat het lokale vervoer rijdt",
          "Zware of omvangrijke bagage",
          "Reizen met kinderen of een kinderwagen",
          "Een trein die u absoluut niet mag missen",
        ],
      },
      {
        heading: "Houd marge aan bij het perron",
        paragraphs: [
          "Reken minimaal tien tot vijftien minuten tussen aankomst bij het station en de vertrektijd van uw trein. Bij grote stations met meerdere perrons en een lange looproute is twintig minuten prettiger, zeker met bagage.",
        ],
      },
      {
        heading: "Waar stapt u in en uit?",
        paragraphs: [
          "Stations hebben vaak een aparte taxistandplaats en een kiss-and-ride-zone, soms aan de achterzijde van het gebouw. Geef bij het boeken de zijde of het adres op waar u wilt worden opgehaald; dat voorkomt onnodig omrijden.",
        ],
      },
      {
        heading: "Bij vertraging of uitval van uw trein",
        paragraphs: [
          "Valt uw trein uit of loopt de vertraging op, dan kan een rechtstreekse taxirit naar de eindbestemming een uitkomst zijn — vooral als u met meerdere personen reist, omdat u dan per voertuig betaalt. Controleer eerst de actuele reisinformatie van de vervoerder.",
        ],
      },
    ],
    conclusion:
      "Gebruik de taxi voor het eerste of laatste stuk en de trein voor de lange afstand. Houd voldoende marge aan bij het station en geef door waar u wilt in- of uitstappen.",
    faq: [
      {
        q: "Hoeveel tijd moet ik tussen taxi en trein aanhouden?",
        a: "Reken op tien tot vijftien minuten, en bij grote stations of veel bagage op ongeveer twintig minuten.",
      },
      {
        q: "Is een taxi handig bij treinuitval?",
        a: "Vaak wel, zeker met een gezelschap: u betaalt per rit en rijdt rechtstreeks naar uw bestemming zonder vervangend vervoer af te wachten.",
      },
    ],
    links: [
      { label: "Taxi of trein: wat is de beste keuze?", to: "/journal/taxi-of-trein" },
      { label: "Taxi boeken", to: "/boeken" },
      { label: "Wat als uw trein of vlucht vertraging heeft?", to: "/journal/vertraging-van-trein-of-vlucht" },
    ],
    sources: [{ label: "Actuele reisinformatie (NS)", url: "https://www.ns.nl/reisinformatie" }],
  },
  {
    slug: "taxi-naar-een-stadscentrum-of-winkelgebied",
    title: "Met de taxi naar een stadscentrum of winkelgebied",
    seoTitle: "Met de taxi naar het stadscentrum of winkelgebied | Taxiritje.nl",
    description:
      "Naar het centrum of een winkelgebied zonder parkeerstress: zo kiest u het juiste in- en uitstappunt en plant u uw rit terug met boodschappen.",
    category: "bestemmingen",
    intent: "Praktisch: bezoek aan binnenstad of winkelcentrum zonder eigen auto.",
    primaryKeyword: "taxi naar stadscentrum",
    published: "2026-08-21",
    readingMinutes: 4,
    intro:
      "In binnensteden is parkeren duur, schaars en soms helemaal niet mogelijk. Wie zich laat brengen en ophalen, slaat de parkeergarage over — en hoeft met volle tassen niet terug te lopen naar een verre parkeerplek.",
    sections: [
      {
        heading: "Voetgangersgebieden en autovrije zones",
        paragraphs: [
          "Veel centra hebben autovrije straten met venstertijden of afsluitingen met beweegbare palen. Een taxi kan u daarom meestal tot de rand van het gebied brengen. Kies een duidelijk punt net buiten de zone, zoals een bekende kruising of een adres aan een doorgaande straat.",
        ],
      },
      {
        heading: "De rit terug met boodschappen",
        paragraphs: [
          "Bij een bezoek aan een winkelgebied is de terugrit vaak het echte knelpunt: dan heeft u tassen bij u en is de bus of tram het minst comfortabel. Reserveer die rit vooraf op een ruim gekozen tijdstip, of boek hem wanneer u ongeveer een half uur van klaar zijn af bent.",
        ],
        bullets: [
          "Spreek een vast ophaalpunt af met uw gezelschap",
          "Houd rekening met drukte op zaterdagen en koopavonden",
          "Grote aankopen? Geef aan hoeveel ruimte u nodig heeft",
        ],
      },
      {
        heading: "Kosten vergelijken met parkeren",
        paragraphs: [
          "Zet de parkeerkosten in een centrumgarage voor een halve dag naast de ritprijs heen en terug. Reist u met twee of meer personen, dan is het verschil vaak klein, terwijl u geen zoektijd en geen loopafstand met tassen heeft.",
        ],
      },
    ],
    conclusion:
      "Laat u afzetten aan de rand van het autovrije gebied en boek de terugrit vooraf. Zo bezoekt u een binnenstad zonder parkeerkosten, zoektijd of sjouwen.",
    faq: [
      {
        q: "Kan een taxi in een voetgangersgebied komen?",
        a: "Vaak niet, of alleen tijdens bepaalde venstertijden. Een ophaalpunt net buiten de zone is doorgaans sneller en duidelijker.",
      },
      {
        q: "Wat als ik grote aankopen heb?",
        a: "Geef bij het boeken aan hoeveel ruimte u nodig heeft, zodat een voertuig met genoeg laadruimte wordt ingezet.",
      },
    ],
    links: [
      { label: "Taxi of eigen auto?", to: "/journal/taxi-of-eigen-auto" },
      { label: "Taxi boeken", to: "/boeken" },
      { label: "Werkgebied bekijken", to: "/taxi" },
    ],
  },
];
